import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import storageService from "~/utils/storage";
import { useGlobalStore } from "~/store";
import useAlert from "~/common/hooks/useAlert";
import useRequest from "~/common/hooks/useRequest";
import { API_STATE } from "~/common/constants/state";

//Assets
import "~/assets/sass/pages/portfolio/portfolioMemo.scss";
import { RoundArrowIcon } from "~/assets/svg/InterfaceIcon";

// Pages
import AddMemo from "./components/AddMemo";
import MemoItem from "./components/MemoItem";

import { useTranslation } from "react-i18next";

const PortfolioMemo = ({ showMemo }) => {
	const [
		{
			user: { info },
		},
	] = useGlobalStore();
	const params = useParams();
	const { idx } = params;
	const memoListRequest = useRequest("get", `/vc/portfolio/memo/${idx}`);
	const memoDeleteRequest = useRequest("patch", "/vc/portfolio/memo/delete");
	const [showDeleteAlert, DeleteAlert] = useAlert();
	const [showAddSuccessAlert, AddSuccessAlert] = useAlert();
	const [showEditSuccessAlert, EditSuccessAlert] = useAlert();
	const [showDeleteSuccessAlert, DeleteSuccessAlert] = useAlert();

	const [memoList, setMemoList] = useState(null);
	const [isShow, setIsShow] = useState(showMemo);

	const { t } = useTranslation();

	const handleAdd = (msg) => {
		showAddSuccessAlert().then(() => {
			memoListRequest.call();
		});
	};

	const handleEdit = (msg) => {
		showEditSuccessAlert().then(() => {
			memoListRequest.call();
		});
	};

	const handleDelete = (memoIdx) => {
		showDeleteAlert().then(() => {
			memoDeleteRequest.call({
				portfolioIdx: parseInt(idx, 10),
				memoIdx,
			});
		});
	};

	const setMemoStateToStorage = (state) => {
		storageService.set(
			"memo",
			{
				closedMemo: state,
			},
			"local"
		);
	};

	const handleClickToggle = () => {
		const condition = !isShow;
		setIsShow(condition);
		const closedMemo = storageService.get("memo", "closedMemo", "local") || {};
		if (condition) {
			if (closedMemo[`idx${idx}`]) {
				delete closedMemo[`idx${idx}`];
				setMemoStateToStorage({
					...closedMemo,
				});
			}
		} else {
			let state = {};
			state[`idx${idx}`] = true;
			setMemoStateToStorage({
				...closedMemo,
				...state,
			});
		}
	};

	useEffect(() => {
		memoListRequest.singleCall();
	}, []);

	useEffect(() => {
		switch (memoListRequest.state) {
			case API_STATE.done:
				const data = memoListRequest.response?.data;
				setMemoList(data);
				break;
			default:
				break;
		}
	}, [memoListRequest.state, memoListRequest.response]);

	useEffect(() => {
		switch (memoDeleteRequest.state) {
			case API_STATE.done:
				showDeleteSuccessAlert().then(() => {
					memoListRequest.call();
				});
				break;
			default:
				break;
		}
	}, [memoDeleteRequest.state, memoDeleteRequest.response]);

	useEffect(() => {
		setIsShow(showMemo);
	}, [showMemo]);

	return (
		<>
			<div
				className={"l__portfolio-memo " + (isShow ? "active" : "")}
				style={
					info.userRequirements === false
						? { top: "160px", height: "calc(100% - 160px)" }
						: { top: "100px", height: "calc(100% - 100px)" }
				}
			>
				{/* 메모 열고 닫기 버튼 */}
				<button
					className="memo-toggle-btn"
					onClick={() => {
						handleClickToggle();
					}}
				>
					<RoundArrowIcon />
				</button>

				{/* 메모 section*/}
				<div className="portfolio-memo-wrap">
					{/* 메모 작성 form-section */}
					<section className="memo-edit-section">
						<h3 className="memo-title">{t("pages.portfolio.PortfolioMemo.memo")}</h3>
						{/* 메모 등록 box */}
						<AddMemo portfolioIdx={idx} onAdd={handleAdd} />
					</section>

					{/* 메모 등록 view-section */}
					{memoList?.length > 0 && (
						<section className="memo-view-section">
							<ul className="memo-list">
								{memoList &&
									memoList.map((memo) => {
										const { root, children } = memo;
										return (
											<li
												className={
													"memo-item " +
													(info.name === root.name ? "active" : "")
												}
												key={root.idx}
											>
												<MemoItem
													portfolioIdx={idx}
													memo={root}
													isMine={info.idx === root.userIdx}
													onAdd={handleAdd}
													onEdit={handleEdit}
													// onModify={handleModify}
													onDelete={handleDelete}
												/>
												{children?.length > 0 && (
													<ul className="reply-list">
														{children.map((child) => {
															return (
																<li
																	className={
																		"reply-item " +
																		(info.name === root.name
																			? "active"
																			: "")
																	}
																	key={child.idx}
																>
																	<MemoItem
																		portfolioIdx={idx}
																		memo={child}
																		isMine={
																			info.name === root.name
																		}
																		isReply={true}
																		onAdd={handleAdd}
																		onEdit={handleEdit}
																		// onModify={handleModify}
																		onDelete={handleDelete}
																	/>
																</li>
															);
														})}
													</ul>
												)}
											</li>
										);
									})}
							</ul>
						</section>
					)}
				</div>
			</div>
			{/*삭제버튼 클릭했을때 alert 모달*/}
			<DeleteAlert
				type="error"
				message={
					<>
						<h3>{t("pages.portfolio.PortfolioMemo.wouldRemove")}</h3>
					</>
				}
				reject={t("pages.portfolio.PortfolioMemo.nein")}
				confirm={t("pages.portfolio.PortfolioMemo.yes")}
				layout="horiz"
				height={230}
			/>
			<AddSuccessAlert
				type="success"
				message={t("pages.portfolio.PortfolioMemo.sucessEnroll")}
				confirm={t("pages.portfolio.PortfolioMemo.check")}
			/>
			<EditSuccessAlert
				type="success"
				message={t("pages.portfolio.PortfolioMemo.sucessMod")}
				confirm={t("pages.portfolio.PortfolioMemo.check")}
			/>
			<DeleteSuccessAlert
				type="success"
				message={t("pages.portfolio.PortfolioMemo.sucessRemove")}
				confirm={t("pages.portfolio.PortfolioMemo.check")}
			/>
		</>
	);
};

export default PortfolioMemo;
